import { HoverIdleModel } from "@/features/hoverIdle/core/hoverIdleModel";
import { getMessages } from "@/i18n/runtime";
import {
  createEmptyHovSkillState,
  createEmptyTimSkillState,
} from "./hoverIdleSaveData";
import type { HovSkillState } from "@/features/hoverIdle/hov/hovSkillModel";
import type { TimSkillState } from "@/features/hoverIdle/tim/timSkillModel";
import type { HoverIdleConfig, HoverIdleModelState } from "../types";
import {
  DEFAULT_ENTITLEMENTS,
  type EntitlementSnapshot,
} from "@/features/payment/types";
import type { AchievementsState } from "@/features/achievements/types";
import type { RecordsState } from "@/features/records/types";
import { decodeHoverIdleSaveData } from "./hoverIdleSaveCodec";
import { computePersistentEffects } from "@/features/achievements/achievementEffects";
import { normalizeAchievementsState } from "@/features/achievements/achievementIdAliases";
import {
  resolveHovSkillEffects,
  type HovSkillResolvedEffects,
} from "@/features/hoverIdle/hov/hovSkillEffects";
import { summarizePermanentPurchaseEffects } from "@/features/payment/paymentPermanentEffects";
import { normalizeOfflineTimeScale } from "@/features/hoverIdle/offline/offlineTimeScale";
import type { HugeNumber } from "@/shared/numbers";
import {
  achievementsStateFromHoverIdleSaveData,
  hovSkillStateFromHoverIdleSaveData,
  timSkillStateFromHoverIdleSaveData,
  modelStateFromHoverIdleSaveData,
  recordsStateFromHoverIdleSaveData,
  emptyAchievementsState,
  emptyRecordsState,
  type HoverIdleSaveData,
  type HoverIdleSaveTimestamps,
} from "./hoverIdleSaveData";
import type { HoverIdleSaveStorage } from "./hoverIdleStorage";
import type { OfflineRewardSaveMetadata } from "./offlineRewardSaveMetadata";
import {
  calculateAndCommitProductionOfflineRewardSave,
  type ProductionOfflineRewardOutcome,
} from "@/features/hoverIdle/offline/productionOfflineReward";

export interface HoverIdleSession {
  readonly model: HoverIdleModel;
  readonly hovSkillState: HovSkillState;
  readonly timSkillState: TimSkillState;
  readonly achievementsState: AchievementsState;
  readonly recordsState: RecordsState;
  readonly timestamps: HoverIdleSaveTimestamps | null;
  readonly offlineReward: OfflineRewardSaveMetadata | null;
  readonly startupOfflineReward: ProductionOfflineRewardOutcome | null;
  readonly loadFailureMessage: string | null;
}

export type HoverIdleSessionLifecycleEvent =
  | "save-read-start"
  | "save-read-complete"
  | "decode-normalize-complete"
  | "save-restore-complete"
  | "offline-reward-elapsed-determined"
  | "offline-reward-calculation-start"
  | "offline-reward-calculation-complete"
  | "offline-reward-validation-complete"
  | "atomic-commit-start"
  | "atomic-commit-complete"
  | "live-model-initialization-start"
  | "live-model-initialization-complete"
  | "committed-model-reconstruction-complete";

export interface CreateHoverIdleSessionOptions {
  readonly storage: HoverIdleSaveStorage;
  readonly nowMs: () => number;
  readonly config?: HoverIdleConfig;
  readonly ownerEntitlementSnapshot?: EntitlementSnapshot;
  readonly startupAdminTimeScale?: number;
  readonly startupSystemTimeScale?: number;
  readonly patchInitialModelState?: (
    state: HoverIdleModelState,
  ) => HoverIdleModelState;
  readonly logLoadFailure?: (reason: string) => void;
  readonly onLifecycleEvent?: (event: HoverIdleSessionLifecycleEvent) => void;
}

export async function createHoverIdleSession(
  options: CreateHoverIdleSessionOptions,
): Promise<HoverIdleSession> {
  const createNewSession = (reason: string | null = null): HoverIdleSession => {
    const messages = getMessages();
    emitLifecycleEvent(options, "live-model-initialization-start");
    let model = createBaseModel(options, emptyAchievementsState);

    if (options.patchInitialModelState !== undefined) {
      const restored = createModelFromState(
        options,
        model.exportState(),
        emptyAchievementsState,
      );
      if (restored.ok) {
        model = restored.model;
      } else {
        options.logLoadFailure?.(restored.reason);
        model = createBaseModel(options, emptyAchievementsState);
      }
    }
    emitLifecycleEvent(options, "live-model-initialization-complete");

    return {
      model,
      hovSkillState: createEmptyHovSkillState(),
      timSkillState: createEmptyTimSkillState(),
      achievementsState: emptyAchievementsState,
      recordsState: emptyRecordsState,
      timestamps: null,
      offlineReward: null,
      startupOfflineReward: null,
      loadFailureMessage:
        reason === null ? null : messages.system.loadFailureMessage,
    };
  };

  let rawSaveData: unknown | null;
  try {
    emitLifecycleEvent(options, "save-read-start");
    rawSaveData = await options.storage.loadLatest();
    emitLifecycleEvent(options, "save-read-complete");
  } catch (error) {
    const reason = reasonFromUnknown(error);
    options.logLoadFailure?.(reason);
    return createNewSession(reason);
  }

  if (rawSaveData === null) {
    emitLifecycleEvent(options, "save-restore-complete");
    return createNewSession();
  }

  const decoded = decodeHoverIdleSaveData(rawSaveData);
  if (!decoded.ok) {
    options.logLoadFailure?.(decoded.reason);
    return createNewSession(decoded.reason);
  }
  emitLifecycleEvent(options, "decode-normalize-complete");
  emitLifecycleEvent(options, "save-restore-complete");

  let activeSave = decoded.data;
  const adminTimeScale = normalizeOfflineTimeScale(
    options.startupAdminTimeScale ?? 1,
  );
  const systemTimeScale = normalizeOfflineTimeScale(
    options.startupSystemTimeScale ?? 1,
  );
  let startupOfflineReward: ProductionOfflineRewardOutcome | null = null;
  try {
    startupOfflineReward = await calculateAndCommitProductionOfflineRewardSave({
      storage: options.storage,
      startSave: activeSave,
      committedAt: options.nowMs(),
      ownerEntitlementSnapshot:
        options.ownerEntitlementSnapshot ?? DEFAULT_ENTITLEMENTS,
      adminTimeScale,
      systemTimeScale,
      onLifecycleEvent: options.onLifecycleEvent,
    });
  } catch (error) {
    const reason = reasonFromUnknown(error);
    options.logLoadFailure?.(reason);
    return createNewSession(reason);
  }
  if (startupOfflineReward.kind === "committed") {
    activeSave = startupOfflineReward.saveData;
  }
  const restoredAchievementsState =
    achievementsStateFromHoverIdleSaveData(activeSave);
  const restoredHovSkillState = hovSkillStateFromHoverIdleSaveData(activeSave);
  emitLifecycleEvent(options, "live-model-initialization-start");
  const restored = restoreSavedModel(
    options,
    activeSave,
    restoredAchievementsState,
    restoredHovSkillState,
  );
  if (!restored.ok) {
    options.logLoadFailure?.(restored.reason);
    return createNewSession(restored.reason);
  }
  emitLifecycleEvent(options, "live-model-initialization-complete");
  if (startupOfflineReward.kind === "committed") {
    emitLifecycleEvent(options, "committed-model-reconstruction-complete");
  }

  return {
    model: restored.model,
    hovSkillState: restoredHovSkillState,
    timSkillState: timSkillStateFromHoverIdleSaveData(activeSave),
    achievementsState: restoredAchievementsState,
    recordsState: recordsStateFromHoverIdleSaveData(activeSave),
    timestamps: activeSave.timestamps,
    offlineReward: activeSave.offlineReward,
    startupOfflineReward,
    loadFailureMessage: null,
  };
}

function restoreSavedModel(
  options: CreateHoverIdleSessionOptions,
  saveData: HoverIdleSaveData,
  achievementsState: AchievementsState,
  hovSkillState: HovSkillState,
): ReturnType<typeof createModelFromState> {
  try {
    const hovSkillEffects = resolveHovSkillEffects({
      acquired: new Set(hovSkillState.acquiredIds),
    });
    return createModelFromState(
      options,
      modelStateFromHoverIdleSaveData(saveData),
      achievementsState,
      resolveSavedMpCapacity(saveData, hovSkillEffects),
      hovSkillEffects,
    );
  } catch (error) {
    return { ok: false, reason: reasonFromUnknown(error) };
  }
}

function emitLifecycleEvent(
  options: CreateHoverIdleSessionOptions,
  event: HoverIdleSessionLifecycleEvent,
): void {
  try {
    options.onLifecycleEvent?.(event);
  } catch {
    // Observability must never change save restore or reward commit semantics.
  }
}
function createBaseModel(
  options: CreateHoverIdleSessionOptions,
  achievementsState: AchievementsState,
): HoverIdleModel {
  const model = new HoverIdleModel(options.config);
  model.setOwnerEntitlementSnapshot(
    options.ownerEntitlementSnapshot ?? DEFAULT_ENTITLEMENTS,
  );
  model.setAchievementPersistentEffects(
    computePersistentEffects(achievementsState.unlockedIds),
  );
  return model;
}

function createModelFromState(
  options: CreateHoverIdleSessionOptions,
  state: HoverIdleModelState,
  achievementsState: AchievementsState,
  initialMpCapacity?: number,
  initialHovSkillEffects?: HovSkillResolvedEffects,
):
  | { readonly ok: true; readonly model: HoverIdleModel }
  | { readonly ok: false; readonly reason: string } {
  const model = createBaseModel(options, achievementsState);
  if (initialMpCapacity !== undefined) {
    model.setMpCapacity(initialMpCapacity);
  }
  if (initialHovSkillEffects !== undefined) {
    model.setHovSkillEffects(initialHovSkillEffects);
  }

  try {
    const initialState = options.patchInitialModelState?.(state) ?? state;
    model.restoreState(initialState);
    model.setAchievementPersistentEffects(
      computePersistentEffects(achievementsState.unlockedIds),
    );
    return { ok: true, model };
  } catch (error) {
    return { ok: false, reason: reasonFromUnknown(error) };
  }
}

function resolveSavedMpCapacity(
  saveData: HoverIdleSaveData,
  hovSkillEffects: HovSkillResolvedEffects,
): number {
  const achievementsState = normalizeAchievementsState({
    unlockedIds: saveData.achievements.unlockedIds as readonly string[],
    seenIds: saveData.achievements.seenIds as readonly string[],
  });
  const achievementEffects = computePersistentEffects(
    achievementsState.unlockedIds,
  );
  const paymentEffects = summarizePermanentPurchaseEffects(
    saveData.payment.purchasedPermanentItems,
  );

  return toFiniteNonNegativeSafeInteger(
    hovSkillEffects.mpCapacity
      .add(paymentEffects.mpCapacity)
      .add(achievementEffects.mpCapacityBonus),
    "saved MP capacity",
  );
}

function toFiniteNonNegativeSafeInteger(
  value: HugeNumber,
  name: string,
): number {
  const numberValue = Number(value.toString());
  if (!Number.isSafeInteger(numberValue) || numberValue < 0) {
    throw new Error(`${name} must be a finite non-negative safe integer`);
  }
  return numberValue;
}

function reasonFromUnknown(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
